/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import SlotEditorStateAPI from './SlotEditorStateAPI';
import SlotEditorModel from '../models/SlotEditorModel';
import { destroy } from 'mobx-state-tree';
import refined_24 from '@ba/ba-graphics/dist/icons-js/refined_24';
import StringResources from '../nls/StringResources';

const APIFactory = require('gemini/lib/@waca/dashboard-common/dist/core/APIFactory');
const ContentActionsProviderAPI = require('gemini/lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI');

const PROVIDER_ID = 'SlotEditor';
const ADD_CONTENT_EVENT_NAME = 'add:content:child';

class SlotEditorState {
	constructor({ features }) {
		this._features = features;
		this._dashboardApi = features.API;
		this._dashboardSettings = features.DashboardSettings;
		this._slideout = features.InAppSlideoutState;
		this._api = null;
		this._model = null;

		this._initialize();
	}

	getAPI() {
		return this._api;
	}

	_initialize() {
		this._api = APIFactory.createAPI(this, [SlotEditorStateAPI]);
		this._model = SlotEditorModel.create({
			slotsModel: {},
			localFiltersModel: {}
		}, {
			dashboardApi: this._dashboardApi
		});

		// WARNING: the DashboardState feature is a runtime dependency for this feature.
		// please use carefully and make sure it is not accessed / loaded on initialization / construction of this feature.
		this._slideout.registerContribution(this.getProviderId(), {
			name: 'slotEditor',
			group: 'slotEditorAction',
			coachMark: this._getCoachMarkOptions(),
			section: ContentActionsProviderAPI.ACTION_SECTION.PANEL,
			order: 30,
			label: StringResources.get('slotEditor'),
			text: StringResources.get('slotEditor'),
			className: 'buttonWithText',
			icon: refined_24.id,
			type: 'Button',
			disabled: () => {
				if (!this._api) {
					// instance destroyed
					return;
				}
				return this._features['DashboardState'].getUiState().eventGroups;
			},
			isEnabled: () => {
				if (!this._api) {
					// instance destroyed
					return;
				}
				// the dashboard app (ie. PA) can decide to forcefully enable the slot editor, regardless of the dashboard mode.
				const isForced = this._settings.forceEnabled;
				const isAuthoringMode = this._features['DashboardState'].getUiState().authoring;
				return isForced || (isAuthoringMode && this.hasCapability(this._dashboardApi, 'canAuthorDashboard'));
			}
		});
		this._dashboardApi.getCanvasWhenReady().then((canvas) => {
			canvas.on(ADD_CONTENT_EVENT_NAME, this._openSlotsSlideOut, this);
		});
		if (this._dashboardSettings) {
			this._settings = this._dashboardSettings.get('slotEditor') || {};
		}
	}

	// TODO: use from a common place. (method copied from dashboard-core)
	hasCapability(api, capability = 'canUseInteractiveDashboard') {
		const getCoreSvc = api.getCoreSvc || api.getGlassCoreSvc;
		const {
			capabilities = []
		} = getCoreSvc.call(api, '.UserProfile');

		return capabilities.indexOf(capability) !== -1;
	}

	getModel() {
		return this._model;
	}

	getProviderId() {
		return PROVIDER_ID;
	}

	destroy() {
		this._slideout.removeContribution(this.getProviderId());

		const canvas = this._dashboardApi.getCanvas();
		if(canvas){
			canvas.off(ADD_CONTENT_EVENT_NAME, this._openSlotsSlideOut, this);
		}
		this._dashboardApi = null;
		this._api = null;
		destroy(this._model);
		this._model = null;
		this._slideout = null;
	}

	_getCoachMarkOptions() {
		return {
			title: StringResources.get('slotEditorCoachMarkTitle'),
			contents: StringResources.get('slotEditorCoachMarkContents'),
			id: 'com.ibm.bi.dashboard.slotEditorAction',
			dashboard: this._dashboardApi
		};
	}

	_openSlotsSlideOut(payload = {}) {
		const spec = payload.info && payload.info.value && payload.info.value.spec || {};
		const isLiveWidget = spec.type === 'live';
		const specData = spec.data || {};
		const isDataEmpty = Object.keys(specData).length === 0;
		if (isLiveWidget && isDataEmpty) {
			this._features['DashboardState'].setSidePanelCurrentView(this.getProviderId());
			if (!this._slideout.isOpen()) {
				this._slideout.open();
			}
		}
	}
}

module.exports = SlotEditorState;



// WEBPACK FOOTER //
// ./src/js/dashboard/SlotEditorState.js